import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const [termine, setTermine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTermin, setNewTermin] = useState({
    datum: '',
    uhrzeit: '',
    name: '',
    grund: 'Telefonisch gebucht'
  });

  const zeitslots = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];

  useEffect(() => {
    loadTermine();
  }, []);

  const loadTermine = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/termine/admin/alle`);
      setTermine(response.data.termine || []);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManuelleEintragung = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/termine/admin/manuell`, {
        datum: newTermin.datum,
        uhrzeit: newTermin.uhrzeit,
        kunde_name: newTermin.name,
        grund: newTermin.grund
      });
      
      alert('Termin erfolgreich eingetragen!');
      setNewTermin({ datum: '', uhrzeit: '', name: '', grund: 'Telefonisch gebucht' });
      loadTermine();
    } catch (error) {
      console.error('Fehler:', error);
      if (error.response?.status === 409) {
        alert('Dieser Termin ist bereits vergeben!');
      } else {
        alert('Fehler beim Eintragen. Bitte versuchen Sie es erneut.');
      }
    }
  };

  const handleDelete = async (terminId) => {
    if (!window.confirm('Termin wirklich löschen?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/termine/admin/${terminId}`);
      alert('Termin gelöscht!');
      loadTermine();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-[#004A6B] text-white p-6 rounded-t-lg">
          <h1 className="text-3xl font-bold">Terminverwaltung</h1>
          <p className="text-blue-200 mt-1">KFZ-Sachverständigenbüro Rinno</p>
        </div>

        <div className="bg-white shadow-lg rounded-b-lg p-6">
          {/* Manuell Termin eintragen */}
          <div className="mb-8 p-6 bg-blue-50 rounded-lg border-2 border-blue-200">
            <h2 className="text-xl font-semibold mb-4 text-[#004A6B]">
              📞 Neuen Termin manuell eintragen
            </h2>
            <form onSubmit={handleManuelleEintragung} className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Datum *</label>
                <Input
                  type="date"
                  required
                  value={newTermin.datum}
                  onChange={(e) => setNewTermin({...newTermin, datum: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Uhrzeit *</label>
                <select
                  required
                  value={newTermin.uhrzeit}
                  onChange={(e) => setNewTermin({...newTermin, uhrzeit: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Bitte wählen</option>
                  {zeitslots.map(slot => (
                    <option key={slot} value={slot}>{slot} Uhr</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Kundenname (optional)</label>
                <Input
                  value={newTermin.name}
                  onChange={(e) => setNewTermin({...newTermin, name: e.target.value})}
                  placeholder="z.B. Max Mustermann"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Grund</label>
                <select
                  value={newTermin.grund}
                  onChange={(e) => setNewTermin({...newTermin, grund: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="Telefonisch gebucht">Telefonisch gebucht</option>
                  <option value="Per Email gebucht">Per Email gebucht</option>
                  <option value="Persönlich gebucht">Persönlich gebucht</option>
                  <option value="Blockiert">Blockiert (kein Kunde)</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <Button type="submit" className="w-full bg-[#0078B5] hover:bg-[#02B4D8] text-lg py-6">
                  Termin eintragen
                </Button>
              </div>
            </form>
          </div>

          {/* Gebuchte Termine */}
          <div>
            <h2 className="text-xl font-semibold mb-4 text-[#004A6B]">
              📅 Gebuchte Termine ({termine.length})
            </h2>
            
            {loading ? (
              <div className="text-center py-8 text-gray-500">Lade Termine...</div>
            ) : termine.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Keine Termine vorhanden</div>
            ) : (
              <div className="space-y-3">
                {termine
                  .sort((a, b) => {
                    const dateA = new Date(a.datum + ' ' + a.uhrzeit);
                    const dateB = new Date(b.datum + ' ' + b.uhrzeit);
                    return dateA - dateB;
                  })
                  .map((termin) => (
                    <div key={termin.id} className="border rounded-lg p-4 hover:bg-gray-50 transition">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-lg font-bold text-[#0078B5]">
                              {new Date(termin.datum).toLocaleDateString('de-DE', {
                                weekday: 'short',
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </span>
                            <span className="text-lg font-bold text-gray-700">
                              {termin.uhrzeit} Uhr
                            </span>
                          </div>
                          
                          {termin.kunde_name && (
                            <div className="text-sm text-gray-700">
                              <strong>Name:</strong> {termin.kunde_name}
                            </div>
                          )}
                          
                          {termin.kunde_email && (
                            <div className="text-sm text-gray-600">
                              <strong>Email:</strong> {termin.kunde_email}
                            </div>
                          )}
                          
                          {termin.kunde_telefon && (
                            <div className="text-sm text-gray-600">
                              <strong>Tel:</strong> {termin.kunde_telefon}
                            </div>
                          )}
                          
                          {termin.nachricht && (
                            <div className="text-sm text-gray-600 mt-1">
                              <strong>Nachricht:</strong> {termin.nachricht}
                            </div>
                          )}
                          
                          {termin.grund && (
                            <div className="text-xs text-gray-500 mt-1 italic">
                              {termin.grund}
                            </div>
                          )}
                          
                          <div className="text-xs text-gray-400 mt-1">
                            Gebucht am: {new Date(termin.gebucht_am).toLocaleString('de-DE')}
                          </div>
                        </div>
                        
                        <Button
                          onClick={() => handleDelete(termin.id)}
                          variant="outline"
                          className="ml-4 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          Löschen
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
